/* eslint-disable max-len */
import React, {
	useState, useCallback, useEffect, useContext
} from 'react';
import classNames from 'classnames';

import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input/react-hook-form';
import { isPossiblePhoneNumber } from 'react-phone-number-input/input-max';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';

import { StoreContext } from '../../context/store-context';

import Button from '../Button/Button';
import Checkbox from '../Checkbox/Checkbox';
import CustomInput from './CustomInput';
import {
	Accordion,
	AccordionItem,
	AccordionControl,
	AccordionPanel
} from '../Accordion';
import Modal from '../Modal';
import renderIcon from '../../utils/renderIcon';

const equipmentSchema = {
	Reformers: yup.array().of(yup.string()),
	'Trapeze and Towers': yup.array().of(yup.string()),
	Chairs: yup.array().of(yup.string()),
	'Training Tools': yup.array().of(yup.string()),
};

const schema = () => yup.object({
	businessName: yup.string().required('*Business Name is required.'),
	contactName: yup.string().required('*Full Name is required.'),
	email: yup.string().email('*Invalid Email.').required('*Email is required.'),
	streetAddress1: yup.string().required('*Address is required.'),
	streetAddress2: yup.string(),
	city: yup.string().required('*City is required.'),
	state: yup.string().required('*State/Province is required.'),
	country: yup.string().required('*Country is required.'),
	postalCode: yup.string().required('*Postal code is required.'),
	location: yup.object().shape({
		lat: yup.number().required(),
		lon: yup.number().required(),
	}),
	telephoneBusiness: yup.string()
		.test('isPhone', '*Invalid Phone number.', value => !value || isPossiblePhoneNumber(value))
		.required('*Phone is required.')
		.typeError('*Invalid Phone number.'),
	websiteUrl: yup.string().url('*Website is not valid.').required('*Website is required.'),
	publish: yup.bool().required('*Permission to Publish is required.'),
	filters: yup.object()
		.shape({
			equipment: yup.object().shape(equipmentSchema)
				.test('atLeastOne', 'Select at least one Equipment', v => {
					return Object.values(v).some(value => value.length);
				}),
		})
	,
	// facilityType: yup.string().required('*Facility Type is required.'),
	// authorizedTrainingCenter: yup.bool().required('*Authorized Training Center is required.'),
	// mailingList: yup.bool().required('*Mailing List is required.'),
	// approved: yup.bool().required('*Approved is required.'),
});

const inputFields = [
	{name: 'businessName', placeholder: 'Business Name'},
	{name: 'contactName', placeholder: 'Full Name'},
	{name: 'email', placeholder: 'Email (not used in the Studio Finder)'},
	{name: 'streetAddress1', placeholder: 'Address (appears in the Studio Finder)', as: Autocomplete},
	{name: 'streetAddress2', placeholder: 'Address 2'},
	{name: 'city', placeholder: 'City'},
	{name: 'state', placeholder: 'State'},
	{name: 'country', placeholder: 'Country'},
	{name: 'postalCode', placeholder: 'Postal Code'},
	{name: 'websiteUrl', placeholder: 'https://www.example.com'},
	{name: 'publish', title: 'Ready to publish?', type: 'radio'},
];

const equipmentsCollection = {
	Reformers: [ 'All', 'Clinical Reformers', 'Konnector Reformer', 'Studio Reformer', 'Allegro Reformer', 'Allegro 2 Reformer', 'Allegro Nextgen Reformer', 'Bravo Reformer', 'Ron Fletcher Reformer', 'Allegro Stretch Reformer', 'CenterLine Reformer', 'Rialto Reformer', 'Contrology Reformer', 'Metro IQ' ],
	'Trapeze and Towers': [ 'All', 'Pilates Trapeze Table (Cadillac)', 'CenterLine Cadillac', 'Reformer Trapeze Combination (RTC)', 'Clinical Reformer with Tower', 'Studio Reformer with Tower', 'CenterLine Reformer with Tower', 'Allegro Reformer with Tower and Mat', 'Allegro 2 Reformer with Tower and Mat', 'Wall Tower', 'Springboard', 'CenterLine Pole System', 'Contrology Cadillac', 'Contrology Cadillac with Toybox', 'Allegro Nextgen Reformer with Tower and Mat', 'Bravo Reformer with Tower and Mat', 'Contrology Reformer with Tower and Mat' ],
	Chairs: [ 'All', 'Combo', 'EXO', 'Wunda', 'CenterLine', 'Arm Chair', 'Contrology Arm Chair', 'Contrology Wunda Chair' ],
	'Training Tools': [ 'All', 'Ladder Barrel', 'MOTR', 'Bodhi', 'CoreAlign', 'Ped-o-pull (square base)', 'Ped-o-pull (kidney base)', 'Contrology Ped-o-pul', 'Contrology Ladder Barrel', 'Contrology Folding Mat', 'Barre' ],
};

const libraries = ['places', 'geometry'];

const ns = `studio-finder-form-v2`;

const StudioFinderFormV2 = () => {
	const {
		register,
		handleSubmit,
		control,
		formState: {
			errors, dirtyFields, isDirty,
		},
		setValue,
		watch,
		resetField,
		reset,
		setError,
		clearErrors,
	} = useForm({
		resolver: yupResolver(schema()),
		defaultValues: {
			businessName: '',
			contactName: '',
			email: '',
			streetAddress1: '',
			streetAddress2: '',
			city: '',
			state: '',
			country: '',
			postalCode: '',
			location: {lat: '', lon: ''},
			telephoneBusiness: '',
			websiteUrl: '',
			publish: false,
			filters: {
				equipment: {
					Reformers: [],
					Chairs: [],
					'Trapeze and Towers': [],
					'Training Tools': [],
				},
			},
		},
		mode: 'onChange',
	});
	// init google maps
	const { isLoaded } = useLoadScript({
		googleMapsApiKey: process.env.GATSBY_GOOGLE_MAPS_KEY,
		libraries,
	});
	// store context
	const { contentfulApi, studioFinderForm, setStudioFinderForm } = useContext(StoreContext);
	// submit state
	const [submitStatus, setSubmitStatus] = useState({
		submitting: false,
		submitted: false,
		error: false,
	});

	const [autoCompleteRef, setAutoCompleteRef] = useState(null);
	const address1Val = watch('streetAddress1');
	const isAddressFieldDirty = !!dirtyFields.streetAddress1;

	// Handle Equipments Selection
	const removeSelectedEquipment = (equip, collection, filters, onChange) => {
		const { equipment } = filters;
		if (equip === 'All') {
			onChange({
				equipment: {
					...equipment,
					[collection]: [],
				},
			});
			return;
		}
		onChange({
			equipment: {
				...equipment,
				[collection]: equipment[collection].filter(_equip => {
					return _equip !== equip && _equip !== 'All';
				}),
			},
		});
	};

	const addSelectedEquipment = (equip, collection, filters, onChange) => {
		const { equipment } = filters;
		const isAllSelected = equipmentsCollection[collection].length - 2 === equipment[collection].length;

		if (equip === 'All' || isAllSelected) {
			onChange({
				equipment: {
					...equipment,
					[collection]: [...equipmentsCollection[collection]],
				},
			});
			return;
		}
		onChange({
			equipment: {
				...equipment,
				[collection]: [...equipment[collection], equip],
			},
		});
	};

	const onEquipmentChange = (e, collection, filters, onChange) => {
		e.target.checked
			? addSelectedEquipment(e.target.value, collection, filters, onChange)
			: removeSelectedEquipment(e.target.value, collection, filters, onChange);
	};
	// End

	// Handle Submit
	const onSubmit = data => {
		setSubmitStatus(prev => ({...prev, submitting: true}));

		const { filters: { equipment }, email } = data;
		const formatData = {
			...data,
			email: email.toLowerCase().trim(),
			filters: {
				equipment: JSON.stringify(equipment),
			},
		};
		const fields = Object.entries(formatData).reduce((accumulated, field) => {
			const [key, value] = field;
			return {
				...accumulated,
				[key]: {
					'en-US': value,
				},
			};
		}, {});
		// create new studio
		contentfulApi
			.createEntry('participantInfo', {
				fields,
			})
			.catch(err => {
				window.console.error(err);
				setSubmitStatus(prev => ({...prev, error: true}));
			})
			.finally(() => setSubmitStatus(prev => ({...prev, submitted: true})));
	};
	// End

	// handle address fields
	const onAutocompleteLoad = useCallback(autoComplete => {
		setAutoCompleteRef(() => autoComplete);
	}, []);

	const onPlaceChanged = async() => {
		const place = autoCompleteRef?.getPlace();
		if (!place?.geometry) {
			setError('streetAddress1', {type: 'custom', message: 'select from suggestions'});
			return;
		}

		clearErrors('streetAddress1');

		const {
			address_components: addressComponents,
			geometry: {
				location: {
					lat,
					lng,
				},
			},
		} = place;

		let address1 = '';
		let address2 = '';
		let postalCode = '';

		const isStreetNumber = addressComponents.some(_address => _address.types.includes('street_number'));

		// eslint-disable-next-line
		for(let i = 0; i < addressComponents.length; i++) {
			const { types, long_name: name } = addressComponents[i];
			if (!isStreetNumber) {
				address1 = place.formatted_address;
				setError('streetAddress1', {type: 'custom', message: 'Address must include street number'});
				break;
			}

			switch (true) {
			case types.includes('street_number'):
				address1 = `${ name } ${ address1 }`;
				break;
			case types.includes('route'):
				address1 = `${ address1 } ${ name }`;
				break;
			case types.includes('subpremise'):
				address2 = `#${ name }`;
				break;
			case types.includes('locality'):
				setValue('city', name, { shouldValidate: true });
				break;
			case types.includes('administrative_area_level_1'):
				setValue('state', name, { shouldValidate: true });
				break;
			case types.includes('country'):
				setValue('country', name, { shouldValidate: true });
				break;
			case types.includes('postal_code'):
				postalCode = `${ name }`;
				break;
			case types.includes('postal_code_suffix'):
				postalCode = `${ postalCode }-${ name }`;
				break;
			default:
				break;
			}
		}

		resetField('streetAddress1', {defaultValue: address1, keepError: true});
		setValue('streetAddress2', address2, { shouldValidate: true });
		setValue('postalCode', postalCode, { shouldValidate: true });
		setValue('location', {lat: lat(), lon: lng()}, {shouldValidate: true});
	};

	useEffect(() => {
		const timeout = setTimeout(() => {
			if (isAddressFieldDirty) {
				autoCompleteRef?.set('place', address1Val);
			}
		}, 300);
		return () => clearTimeout(timeout);
	}, [address1Val, autoCompleteRef, isAddressFieldDirty]); // eslint-disable-line
	// End

	// Render Components
	const renderInputFiled = ({
		name, placeholder, readOnly = false, as,
	}) => {
		const ComponentTag = as || 'div';
		const prop = as
			? {
				onLoad: onAutocompleteLoad,
				onPlaceChanged,
				fields: ['address_components', 'formatted_address', 'geometry'],
			}
			: {};
		return (
			<ComponentTag
				{...prop}
				key={name}
				className={`form-group`}
			>
				<>
					<input
						{...register(name)}
						readOnly={readOnly}
						type={`text`}
						className={`form-control ${ errors[name] ? 'error' : '' }`}
						placeholder={placeholder}
						onFocus={e => {
							e.target.setAttribute('autocomplete', 'new-password');
							e.target.name = '';
						}}
						onBlur={e => {
							e.target.name = name;
							setValue(name, e.target.value, {shouldValidate: true, shouldDirty: true});
						}}
					/>
					{errors[name]?.message && (
						<p className={`form-error`}>{errors[name].message}</p>
					)}
				</>
			</ComponentTag>
		);
	};

	const renderRadioButtons = ({ name, title, readOnly = false }) => {
		return (
			<div key={name} className={`form-group`}>
				<h5>{title}</h5>
				<Controller
					name={name}
					control={control}
					render={({
						field: {
							onChange,
							value,
							ref,
							...rest
						},
					}) => {
						return (
							<>
								<Checkbox
									{...rest}
									disabled={readOnly}
									circle
									label={'Yes, ready to publish.'}
									checked={value === true}
									onChange={() => {
										return onChange(true);
									}}
								/>
								<Checkbox
									{...rest}
									disabled={readOnly}
									circle
									label={'No, save as a draft.'}
									checked={value === false}
									onChange={() => {
										return onChange(false);
									}}
								/>
							</>
						);
					}}
				/>
			</div>
		);
	};

	const renderField = args => {
		const { type } = args;
		if (type === 'radio') return renderRadioButtons(args);
		return renderInputFiled(args);
	};
	// End

	const renderSubmitButtonText = () => {
		if (submitStatus.error) return 'Failed';
		if (submitStatus.submitted) return 'Added Successfully';
		if (submitStatus.submitting) return 'Submitting...';
		return 'Submit for Approval';
	};

	const closeModal = () => {
		setStudioFinderForm(() => false);
		resetField('streetAddress1', {defaultValue: ''});
		reset();
		setSubmitStatus(() => ({
			submitting: false,
			submitted: false,
			error: false,
		}));
	};

	const disabled = submitStatus.error || submitStatus.submitting || submitStatus.submitted || !isDirty;

	const rootClassnames = classNames({
		[ns]: true,
		[`${ ns }--disabled`]: submitStatus.submitting,
	});

	if (!contentfulApi && !isLoaded) return null;

	return (
		<Modal
			contentLabel={'studio-finder-form'}
			variant={`drawer`}
			onRequestClose={closeModal}
			isOpen={studioFinderForm}
		>
			<div className={rootClassnames}>
				<h3>Studio Finder Application</h3>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className={`form-fields`}>
						{inputFields.map(renderField)}
						<div className={`form-group`}>
							<PhoneInput
								international
								control={control}
								name={'telephoneBusiness'}
								type={'text'}
								placeholder={'phone number (Include Country Code)'}
								error={errors.telephoneBusiness}
								inputComponent={CustomInput}
								defaultCountry={'US'}
							/>
							{errors.telephoneBusiness?.message && (
								<p className={`form-error`}>{errors.telephoneBusiness.message}</p>
							)}
						</div>
						{/* Equipments Accordion */}
						<Controller
							name={`filters`}
							control={control}
							render={({
								field: {
									onChange,
									value: filters,
									ref,
									...rest
								},
							}) => {
								return (
									<div
										{...rest}
										role={'button'}
										tabIndex={0}
										ref={ref}
										className={`form-group ${ errors.filters && 'form-group--error' }`}
									>
										<h5>
											Studio Equipment Profile
											{' '}
											{errors.filters && <span>(*Required)</span>}
										</h5>
										<Accordion>
											{Object.entries(equipmentsCollection)
												.map(entry => {
													const [key, equipmentsArr] = entry;
													return (
														<AccordionItem key={key}>
															<AccordionControl>
																<span>{key}</span>
																<span aria-hidden>
																	{renderIcon('plus')}
																</span>
															</AccordionControl>
															<AccordionPanel>
																{equipmentsArr.map(equip => {
																	const isChecked = filters.equipment[key]?.includes(equip);
																	return (
																		<Checkbox
																			key={equip}
																			label={equip}
																			value={equip}
																			checked={isChecked}
																			onChange={e => onEquipmentChange(e, key, filters, onChange)}
																		/>
																	);
																})}
															</AccordionPanel>
														</AccordionItem>
													);
												})}
										</Accordion>
									</div>
								);
							}}
						/>
						{/* End */}
					</div>
					<Button
						type={`submit`}
						disabled={disabled}
					>
						{renderSubmitButtonText()}
					</Button>
				</form>
			</div>
		</Modal>
	);
};

export default StudioFinderFormV2;
